import { List, Map } from "immutable";
import moment from "moment";
import { createSelector } from "reselect";
import i18n from "~/i18n.js";
import { MEMBER_PERMISSIONS } from "~/records/record.project";
import { getCompanyById } from "~/store/selectors/selector.companies";
import {
  isClient,
  isFinancialManager,
  isMember,
  isMemberWithoutPermissions,
  isOwner,
  isPowerClient,
  isPowerUser,
} from "~/utils/userHelper.js";
import { replaceAvatarSize, sortCompare } from "~/utils/utils.jsx";
import { createImmutableIsEqualSelector } from "./index.jsx";
import { getConfigOption, getRawConfigOption } from "./selector.configOptions.jsx";
import {
  getActiveProjectById,
  getProjectById,
  getProjectLeaderById,
  getProjectMembersById,
} from "./selector.projects.jsx";
import { getTaskById } from "./selector.tasks.jsx";
import { getAllWeeklyUserCapacityData } from "./selector.weeklyWorkerCapacity.js";

export const getUserById = (state, id) => state.users.get(`${id}`);
export const getAllUsers = (state) => state.users;
export const getAllUsersMap = (state) => state.users.toMap().toJS();
export const getLoggedUser = (state) => state.logged_user;
export const getUsersByIds = (state, ids) => {
  return getAllUsers(state).filter((u) => {
    return ids && (List.isList(ids) || Array.isArray(ids)) && ids.indexOf(u.id) > -1;
  });
};

export const getLeadersByIds = createSelector(
  getAllUsers,
  (_state, userIds) => userIds,
  (users, userIds) => {
    const leaders = userIds
      .map((id) => {
        if (users.get(`${id}`)) {
          return { id, name: users.get(`${id}`).display_name };
        }
      })
      .filter(Boolean)
      .sort((a, b) => sortCompare(a.name.toLowerCase(), b.name.toLowerCase()));

    return userIds.includes(0) ? [{ id: 0, name: i18n.t("No leader") }, ...leaders] : leaders;
  }
);

export const getAllUsersIdsNamesArray = createSelector(getAllUsers, (users) => {
  return users
    .toArray()
    .sort((a, b) => sortCompare(a.display_name, b.display_name))
    .map((user) => {
      return {
        id: user.id,
        name: user.display_name,
      };
    });
});

export const getActiveUsers = createSelector(getAllUsers, (users) =>
  users.filter((user) => !user.get(`is_archived`) && !user.get(`is_trashed`))
);

export const getActiveUsersIds = createSelector(getActiveUsers, (users) => users.map((u) => u.id).toArray());

export const getActiveUsersWithoutVirtual = createSelector(getAllUsers, (users) =>
  users.filter((user) => !user.get(`is_archived`) && !user.get(`is_trashed`) && !user.get(`is_email_at_example`))
);

export const getActiveUsersMapSortedBy = createSelector(getActiveUsers, (users) =>
  users.sortBy((user) => user.get(`display_name`).toLowerCase())
);

export const getActiveUsersSortedBy = createSelector(getActiveUsers, (users) =>
  users.sortBy((user) => user.get(`display_name`).toLowerCase()).toArray()
);

export const getActiveOwnersAndMembers = createSelector(getActiveUsers, (users) =>
  users
    .filter((user) => user.isOwner() || user.isMember())
    .toList()
    .toJS()
);

export const getActiveOwnersAndMembersForPicker = createSelector(getActiveUsers, (users) =>
  users.filter((user) => user.isOwner() || user.isMember()).toList()
);

export const getActiveUsersWithPermissions = createSelector(getActiveUsers, (users) =>
  users
    .filter((user) => user.isOwner() || user.isMember() || user.custom_permissions.indexOf("can_manage_tasks") > -1)
    .sort((a, b) => sortCompare(a.get("display_name").toLowerCase(), b.get("display_name").toLowerCase()))
    .toList()
    .toJS()
);

const getSelectedSubscriberListIds = (state, ids) => {
  if (List.isList(ids)) {
    return ids;
  }

  return ids instanceof Array ? new List(ids) : List();
};

export const getLoggedUserId = (state) => getLoggedUser(state).id;

export const makeProjectMembersSelector = () => {
  return createImmutableIsEqualSelector(
    [getProjectMembersById, getAllUsers, getLoggedUserId],
    (projectMembers, users, loggedUserId) => {
      let filteredUsers = users.filter((user) => projectMembers.indexOf(user.id) > -1); // ordered map
      return filteredUsers
        .sortBy((user) => {
          return typeof user.display_name === "string" ? user.display_name.toLowerCase() : "";
        })
        .sort((a) => {
          if (a.get(`id`) === loggedUserId) {
            return -1;
          }
          return 0;
        });
    }
  );
};

export const getSortedProjectMembers = createSelector(
  getProjectMembersById,
  getAllUsers,
  getLoggedUserId,
  getProjectLeaderById,
  (projectMembers, users, loggedUserId, projectLeaderId) => {
    return users
      .filter((user) => projectMembers.indexOf(user.id) > -1)
      .sortBy((user) => (typeof user.display_name === "string" ? user.display_name.toLowerCase() : ""))
      .sort((a, b) => {
        if (a.get(`id`) === loggedUserId) {
          return -1;
        }

        if (b.get("id") === loggedUserId) {
          return 1;
        }

        return 0;
      })
      .sort((a, b) => {
        if (a.get(`id`) === projectLeaderId) {
          return -1;
        }

        if (b.get("id") === projectLeaderId) {
          return 1;
        }

        return 0;
      })
      .toList()
      .toJS();
  }
);

export const makeTaskFormSubscriberListIdsSelector = () => {
  return createSelector([getLoggedUserId, getSelectedSubscriberListIds], (loggedUserId, subscriberIds) => {
    if (subscriberIds.indexOf(loggedUserId) === -1) {
      subscriberIds = subscriberIds.push(loggedUserId).sortBy();
    }
    return subscriberIds;
  });
};

export const makeUsersByIdsSelector = () => {
  return createImmutableIsEqualSelector(getUsersByIds, (users) => users);
};

export const makeGetUsersForMention = (userIds, unavailableUserIds) => {
  return createSelector(
    (state) => getUsersByIds(state, userIds),
    getLoggedUserId,
    () => unavailableUserIds,
    (users, loggedUserId, unavailableUserIds) => {
      return users
        .filter((user) => user.id !== loggedUserId)
        .map((user) => {
          return {
            id: user.id,
            type: "user",
            name: user.getName(),
            avatar_url: user.getAvatarUrl(`small`),
            unavailable: unavailableUserIds ? unavailableUserIds.includes(user.id) : false,
          };
        });
    }
  );
};

export const makeUserIdListExcludingClientsSelector = () => {
  return createImmutableIsEqualSelector([getUsersByIds], (users) => {
    return users
      .filter((u) => !u.isClient())
      .keySeq()
      .map((id) => parseInt(id))
      .toList();
  });
};

export const getLoggedUserType = createSelector(getLoggedUser, (user) => user && user.class);

export const makeIsLoggedUserOwnerSelector = () => {
  return createSelector(getLoggedUserType, (type) => type === `Owner`);
};

export const makeIsLoggedUserClientSelector = () => {
  if (process.env.NODE_ENV === "development") {
    // eslint-disable-next-line no-console
    console.warn(
      `
        Selector "makeIsLoggedUserClientSelector" is deprecated and it will be removed in the future.
        Use "isLoggedUserClient" selector instead.

        The logged user is a constant and so this does not need to be a make selector function. This creates a new
        selector each time it is used and stores it in memory.
      `
    );
  }
  return createSelector(getLoggedUserType, (type) => type === `Client`);
};

export const isLoggedUserClientSelector = createSelector(getLoggedUserType, (type) => type === "Client");

export const makeUserNameSelector = () => {
  return createSelector(getUserById, (user) => (user ? user.getName() : null));
};

export const makeUserLinkSelector = () => {
  return createSelector(getUserById, (user) => (user ? user.getViewUrl() : null));
};

export const makeShortUserNameSelector = () => {
  return createSelector(getUserById, (user) => (user ? user.getName(true) : null));
};

export const makeCanLoggedUserEditLabelsSelector = () => {
  return createSelector(getLoggedUser, (user) => user.isOwner() || user.isClientPlus() || user.isMember());
};

export const getIsLoggedUserClientPlus = createSelector(
  (state) => getLoggedUser(state).class,
  (state) => getLoggedUser(state).custom_permissions,
  (type, customPermissions) => {
    return type === `Client` && customPermissions.indexOf(`can_manage_tasks`) > -1;
  }
);

export const makeHasLoggedUserElevatedPermissionsSelector = () => {
  if (process.env.NODE_ENV === "development") {
    // eslint-disable-next-line no-console
    console.warn(
      `
        Selector "makeHasLoggedUserElevatedPermissionsSelector" is deprecated and it will be removed in the future.
        Use "hasLoggedUserElevatedPermissions" selector instead.

        The logged user is a constant and so this does not need to be a make selector function. This creates a new
        selector each time it is used and stores it in memory.
        `
    );
  }
  return createSelector(
    (state) => getLoggedUser(state).class,
    (state) => getLoggedUser(state).custom_permissions,
    (type, customPermissions) => {
      let isClient = type === `Client`;
      let isClientPlus = isClient && customPermissions.indexOf(`can_manage_tasks`) > -1;
      return !isClient || isClientPlus;
    }
  );
};

export const countClientPlusUsers = createSelector(getAllUsers, (users) =>
  users.reduce((acc, user) => {
    if (user.isClientPlus()) {
      return ++acc;
    }

    return acc;
  }, 0)
);

export const hasLoggedUserElevatedPermissions = createSelector(
  (state) => getLoggedUser(state).class,
  (state) => getLoggedUser(state).custom_permissions,
  (type, customPermissions) => {
    const isClient = type === "Client";
    const isClientPlus = isClient && customPermissions.indexOf("can_manage_tasks") > -1;
    return !isClient || isClientPlus;
  }
);

export const hasCollaborativePermissionsOnProject = createSelector(
  getLoggedUser,
  hasLoggedUserElevatedPermissions,
  (state, projectId) => getProjectById(state, projectId),
  (user, hasElevatedPermissions, project) => {
    if (
      project?.member_permissions === MEMBER_PERMISSIONS.STRICT &&
      isMemberWithoutPermissions(user) &&
      project?.leader_id !== user?.id
    ) {
      return false;
    }

    return hasElevatedPermissions;
  }
);

export const makeIsLoggedUserPowerUser = () => {
  return createSelector(
    (state) => getLoggedUser(state).class,
    (state) => getLoggedUser(state).custom_permissions,
    (type, customPermissions) => {
      if (type === `Owner`) {
        return true;
      }

      return type === `Member` && customPermissions.indexOf(`can_manage_projects`) > -1;
    }
  );
};

export const canManageProjectSelector = createSelector(
  getProjectById,
  (state) => getLoggedUser(state),
  (project, loggedUser) => {
    if (loggedUser.isOwner()) return true;
    if (project?.leader_id === loggedUser.id) return true;
    return (
      loggedUser.isMember() &&
      loggedUser.custom_permissions.indexOf(`can_manage_projects`) > -1 &&
      project?.created_by_id === loggedUser.id
    );
  }
);

export const makeShowWarningOnLeaderChange = () => {
  return createSelector(
    (state, projectId) => getProjectById(state, projectId),
    (state) => getLoggedUser(state),
    (project, loggedUser) => {
      const canManageProjects = loggedUser.custom_permissions.indexOf(`can_manage_projects`) > -1;
      return (
        loggedUser.isMember() && (!canManageProjects || (canManageProjects && project?.created_by_id !== loggedUser.id))
      );
    }
  );
};

export const makeGetLoggedUserLanguageIdSelector = () => {
  return createSelector(getLoggedUser, (loggedUser) => loggedUser.language_id);
};

const prepareUserAvatarUrl = (avatarUrl, size, userId, userName, userEmail, avatarVersion) => {
  let sizeInPixels = 24;

  if (size > 24 && size <= 40) {
    sizeInPixels = 40;
  } else if (size > 40 && size <= 80) {
    sizeInPixels = 80;
  } else if (size > 80) {
    sizeInPixels = 256;
  }

  return avatarUrl
    .replace(`--SIZE--`, sizeInPixels)
    .replace(`--USER-ID--`, userId)
    .replace(`--USER-NAME--`, encodeURIComponent(userName))
    .replace(`--USER-EMAIL--`, encodeURIComponent(userEmail))
    .replace(`--AVATAR-VERSION--`, avatarVersion);
};

export const makeGetUserNameAndAvatarUrlByIdSelector = () => {
  const defaultUser = Map();
  const getUserByIdOrDefault = (state, id) => state.users.get(`${id}`, defaultUser);
  return createSelector(
    (state) => getConfigOption(state, `avatar_url`),
    (...attrs) => getUserByIdOrDefault(...attrs).get(`id`),
    (...attrs) => getUserByIdOrDefault(...attrs).get(`display_name`),
    (...attrs) => getUserByIdOrDefault(...attrs).get(`email`),
    (...attrs) => getUserByIdOrDefault(...attrs).get(`avatar_version`),
    (state, id, size) => size,
    (avatar_url, id, name, email, timestamp = 0, size = 40) => ({
      name,
      url: prepareUserAvatarUrl(avatar_url, size, id, name, email, timestamp),
    })
  );
};

export const makeAvatarUrlTemplateSelector = () => {
  return createSelector(
    (state) => getConfigOption(state, `avatar_url`), // avatarUrl
    (state, params) => (params && params.size ? params.size : ``), // size
    (state, params) => (params && params.userId ? params.userId : 0), // userId
    (state, params) => (params && params.userName ? params.userName : ``), // userName
    (state, params) => (params && params.userEmail ? params.userEmail : ``), //userEmail
    (state, params) => (params && params.avatarVersion ? params.avatarVersion : ``),
    prepareUserAvatarUrl
  );
};

export const makeGetUserDailyCapacitySelector = (id) => {
  return createSelector(
    (state) => getUserById(state, id),
    (state) => getRawConfigOption(state, `user_daily_capacity`),
    (user, globalDailyCapacity) => user.get(`daily_capacity`) || globalDailyCapacity
  );
};

export const makeGetUserNameAndLinkByTaskIdSelector = () => {
  return createSelector(
    (state, { id }) => getTaskById(state, id),
    (state, { created_by_id }) => getUserById(state, created_by_id),
    isLoggedUserClientSelector,
    (task, user, is_logged_user_client) => {
      let link = ``;
      if (user) {
        link = is_logged_user_client ? `mailto:${user.get("email")}` : user.get("url_path");
      } else if (task.get("created_by_email")) {
        link = `mailto:${task.get("created_by_email")}`;
      }
      let name = user ? user.getName() : ``;
      if (!name && task.get("created_by_name")) {
        name = task.get("created_by_name");
      }
      return {
        link,
        name,
      };
    }
  );
};

export const makeIsLoggedUserOwnerOrProjectLeadForProject = () => {
  return createSelector(getLoggedUser, getProjectLeaderById, (user, project_leader_id) => {
    return user.id === project_leader_id || user.isOwner();
  });
};

export const getUsersWithCustomDailyCapacity = createSelector(getActiveUsers, (users) =>
  users
    .filter((user) => user.get(`daily_capacity`))
    .map((user) => ({
      id: user.id,
      name: user.display_name,
      value: user.daily_capacity,
    }))
    .toArray()
);

export const getUsersWithCustomWeeklyCapacity = createSelector(
  getAllUsersIdsNamesArray,
  getAllWeeklyUserCapacityData,
  (userNames, allUsersWeekCapacities) => {
    return userNames
      .filter((user) => allUsersWeekCapacities[user.id]?.capacity_source === "custom")
      .map((user) => ({
        id: user.id,
        name: user.name,
        weekly_capacity: allUsersWeekCapacities[user.id]?.weekly_capacity,
        verbose_capacity: allUsersWeekCapacities[user.id]?.verbose_capacity,
      }));
  }
);

export const getNumberOfOwners = createSelector(getAllUsers, (users) =>
  users.reduce((val, user) => {
    return (val += user.get(`class`) === `Owner` && !user.get(`is_trashed`) && !user.get(`is_archived`) ? 1 : 0);
  }, 0)
);

export const isLoggedUserSubcontractor = createSelector(
  getLoggedUser,
  (state) => getRawConfigOption(state, "owner_company_id"),
  (loggedUser, ownerCompanyId) => isMember(loggedUser) && loggedUser.company_id !== ownerCompanyId
);

export const isEstimateVisibleToSubcontractors = createSelector(
  isLoggedUserSubcontractor,
  (state, projectId) => getProjectById(state, projectId),
  (isSubcontractor, project) => {
    const isVisibleToSubcontractors = project ? project.get("is_estimate_visible_to_subcontractors") : null;

    return !(isSubcontractor && !isVisibleToSubcontractors);
  }
);

export const canSeeTimeEstimates = createSelector(
  isLoggedUserClientSelector,
  (state, projectId) => isEstimateVisibleToSubcontractors(state, projectId),
  (state, projectId) => getProjectById(state, projectId),
  (state) => getConfigOption(state, `task_estimates_enabled`),
  (isLoggedUserClient, estimateVisibleToSubcontractors, project, taskEstimatesEnabled) => {
    if (!taskEstimatesEnabled) {
      return false;
    }
    if (!project?.task_estimates_enabled) {
      return false;
    }
    if (!estimateVisibleToSubcontractors) {
      return false;
    } else if (!project.show_task_estimates_to_clients) {
      return !isLoggedUserClient;
    }
    return true;
  }
);
export const canSeeCallToUpgradeForTimeEstimates = createSelector(
  isLoggedUserClientSelector,
  (state) => getConfigOption(state, `task_estimates_enabled`),
  (isLoggedUserClient, taskEstimatesEnabled) => {
    if (!taskEstimatesEnabled) {
      return !isLoggedUserClient;
    }

    return false;
  }
);

export const makeGetUserAvatarDataSelector = () =>
  createSelector(
    (state, { id: userId }) => getUserById(state, userId),
    (state, { id: userId }) => userId,
    (state, { name }) => name,
    (state, { short }) => short,
    (user = Map(), userId = 0, name = "", short = false) => {
      const userName = short ? user.get("short_display_name") : user.get("display_name");

      if (typeof userName === `string` && userName.length > 0) {
        return {
          name: userName,
          viewUrl: user.get("url_path"),
        };
      }

      return {
        name,
        viewUrl: `/users/${userId}`,
      };
    }
  );

export const makeCanViewProjectElement = () => {
  return createSelector(
    getLoggedUserId,
    makeIsLoggedUserOwnerSelector(),
    getProjectById,
    (loggedUserId, isLoggedUserOwner, project) => {
      if (isLoggedUserOwner) {
        return true;
      }
      if (!project) {
        return false;
      }
      return project.members.indexOf(loggedUserId) > -1;
    }
  );
};

const getUsersFromProjectMembers = createSelector([getProjectById, getAllUsers], (project, users) => {
  return users.filter((user) => {
    return project?.get("members").some((id) => id === user.get("id"));
  });
});

export const getUsersFromProjectMembersForUserSelector = createSelector(getUsersFromProjectMembers, (users) => {
  return users
    .sort((a, b) => sortCompare(a.get("display_name").toLowerCase(), b.get("display_name").toLowerCase()))
    .toArray()
    .filter((user) => user.get("class") !== "Client")
    .map((user) => ({
      id: user.get("id"),
      name: user.get("display_name"),
      avatarUrl: user.get("avatar_url"),
    }));
});

export const getUsersFromProjectMembersForTaskUserSelector = createSelector(
  getUsersFromProjectMembers,
  getLoggedUser,
  (users, loggedUser) => {
    const preparedUsers = users
      .sort((a, b) => sortCompare(a.get("display_name").toLowerCase(), b.get("display_name").toLowerCase()))
      .toArray()
      .filter(
        (user) =>
          user.get("class") !== "Client" ||
          (user.get("class") === "Client" && user.get("custom_permissions").indexOf(`can_manage_tasks`) > -1)
      )
      .map((user) => ({
        id: user.get("id"),
        name: user.get("display_name"),
        image: replaceAvatarSize(user.get("avatar_url"), 24),
        class: user.get("class"),
      }));
    if (preparedUsers.find(({ id }) => id === loggedUser.get("id"))) {
      const filterArrayOfUsers = preparedUsers.filter(({ id }) => id !== loggedUser.get("id"));
      return [
        {
          id: loggedUser.get("id"),
          name: loggedUser.get("display_name"),
          image: replaceAvatarSize(loggedUser.get("avatar_url"), 24),
          class: loggedUser.get("class"),
        },
        ...filterArrayOfUsers,
      ];
    }
    return preparedUsers;
  }
);

export const getActiveUsersMembersForTaskUserSelector = createSelector(
  getActiveOwnersAndMembersForPicker,
  getLoggedUser,
  (users, loggedUser) => {
    const preparedUsers = users
      .sort((a, b) => sortCompare(a.get("display_name").toLowerCase(), b.get("display_name").toLowerCase()))
      .toArray()
      .filter(
        (user) =>
          user.get("class") !== "Client" ||
          (user.get("class") === "Client" && user.get("custom_permissions").indexOf(`can_manage_tasks`) > -1)
      )
      .map((user) => ({
        id: user.get("id"),
        name: user.get("display_name"),
        image: replaceAvatarSize(user.get("avatar_url"), 24),
        class: user.get("class"),
      }));
    if (preparedUsers.find(({ id }) => id === loggedUser.get("id"))) {
      const filterArrayOfUsers = preparedUsers.filter(({ id }) => id !== loggedUser.get("id"));
      return [
        {
          id: loggedUser.get("id"),
          name: loggedUser.get("display_name"),
          image: replaceAvatarSize(loggedUser.get("avatar_url"), 24),
          class: loggedUser.get("class"),
        },
        ...filterArrayOfUsers,
      ];
    }
    return preparedUsers;
  }
);

export const getActiveUsersMembersByUserIdsSelector = createSelector(
  getUsersByIds,
  getLoggedUser,
  (users, loggedUser) => {
    const preparedUsers = users
      .sort((a, b) => sortCompare(a.get("display_name").toLowerCase(), b.get("display_name").toLowerCase()))
      .toArray()
      .filter(
        (user) =>
          user.get("class") !== "Client" ||
          (user.get("class") === "Client" && user.get("custom_permissions").indexOf(`can_manage_tasks`) > -1)
      )
      .map((user) => ({
        id: user.get("id"),
        name: user.get("display_name"),
        image: replaceAvatarSize(user.get("avatar_url"), 24),
        class: user.get("class"),
      }));
    if (preparedUsers.find(({ id }) => id === loggedUser.get("id"))) {
      const filterArrayOfUsers = preparedUsers.filter(({ id }) => id !== loggedUser.get("id"));
      return [
        {
          id: loggedUser.get("id"),
          name: loggedUser.get("display_name"),
          image: replaceAvatarSize(loggedUser.get("avatar_url"), 24),
          class: loggedUser.get("class"),
        },
        ...filterArrayOfUsers,
      ];
    }
    return preparedUsers;
  }
);

export const getFilteredUsersFromProjectMembersForTaskUserSelector = createSelector(
  getUsersFromProjectMembersForTaskUserSelector,
  (_, projectId, excludeClients) => excludeClients,
  (users, excludeClients = false) => {
    return excludeClients ? users.filter((user) => user.class !== "Client") : users;
  }
);

export const canAccessCalendar = createSelector(
  (state) => getRawConfigOption(state, "calendar_enabled"),
  (value) => value
);

export const canAccessTrash = createSelector(getLoggedUser, (user) => isOwner(user) || isMember(user));

export const canAccessStopwatches = createSelector(isLoggedUserClientSelector, (isClient) => !isClient);

export const canManageTaskLabels = createSelector(
  canManageProjectSelector,
  getActiveProjectById,
  (canManageProject, project) => {
    return canManageProject && !!project;
  }
);

export const canAccessReports = createSelector(getLoggedUser, (user) => isPowerUser(user) || isFinancialManager(user));

export const canAddTimeRecordsForProject = createSelector(getLoggedUser, getProjectById, (user, project) => {
  return !isClient(user) && project?.is_tracking_enabled;
});

export const canAddExpensesForProject = createSelector(
  getLoggedUser,
  getProjectById,
  (state) => getRawConfigOption(state, "expense_tracking_enabled"),
  (user, project, configOption) => {
    return !isClient(user) && project?.is_tracking_enabled && configOption;
  }
);

export const canAccessInvoices = createSelector(
  getLoggedUser,
  (state) => getRawConfigOption(state, "invoices_enabled"),
  (user, invoicesEnabled) => isFinancialManager(user) && invoicesEnabled
);

export const canAccessEstimates = createSelector(
  getLoggedUser,
  (state) => getRawConfigOption(state, "estimates_enabled"),
  (user, estimatesEnabled) => isFinancialManager(user) && estimatesEnabled
);

export const canAccessRecurringProfiles = createSelector(
  getLoggedUser,
  (state) => getRawConfigOption(state, "invoices_enabled"),
  (user, invoicesEnabled) => isFinancialManager(user) && invoicesEnabled
);

export const canAccessMyWork = createSelector(getLoggedUser, (user) => !isClient(user) || isPowerClient(user));

export const canAccessIntegrations = createSelector(getLoggedUser, (user) => !isClient(user));

export const canAccessPeopleSection = createSelector(
  getLoggedUser,
  (state) => getRawConfigOption(state, "owner_company_id"),
  (user, ownerCompanyId) => {
    if (isOwner(user)) {
      return true;
    }

    if (isClient(user)) {
      return false;
    }

    return isMember(user) && user.company_id === ownerCompanyId;
  }
);

export const canManagePeople = createSelector(getLoggedUser, (user) => isPowerUser(user));
export const canManageFinancial = createSelector(getLoggedUser, (user) => isFinancialManager(user));

export const canAccessTimesheet = createSelector(
  getLoggedUser,
  canManagePeople,
  (state) => getRawConfigOption(state, "timesheet_enabled"),
  (loggedUser, canManagePeople, timesheetEnabled) =>
    timesheetEnabled && (canManagePeople || isFinancialManager(loggedUser))
);

export const canAccessWorkload = (state) => {
  return (canManagePeople(state) || canManageFinancial(state)) && getRawConfigOption(state, "workload_enabled");
};

export const canAccessCapacity = (state) => {
  return canManagePeople(state) || canManageFinancial(state);
};

export const canAccessChat = createSelector(getLoggedUser, (user) => !isClient(user));

export const searchActiveUsersByName = createSelector(
  getActiveUsers,
  (state, searchBy) => searchBy,
  (activeUsers, searchBy) => {
    if (!searchBy) {
      return [];
    }

    return activeUsers
      .filter((u) => {
        const hasSearchTermInDisplayName = u.get("display_name").toLowerCase().includes(searchBy.toLowerCase());
        if (!hasSearchTermInDisplayName) {
          const emailParts = u.get(`email`).split("@");
          return emailParts[0].includes(searchBy.toLowerCase());
        }
        return hasSearchTermInDisplayName;
      })
      .toList()
      .toJS();
  }
);

export const canUseIdModeInQuickSearch = createSelector(
  canAccessInvoices,
  (state) => getConfigOption(state, "show_task_id"),
  (state) => getConfigOption(state, "show_project_id"),
  (canAccessInvoices, showTaskId, showProjectId) => {
    return showProjectId || showTaskId || canAccessInvoices;
  }
);

export const makeGetUsersWithJustSelectedJobType = (data, jobTypeId) =>
  createSelector(getAllUsers, (users) => {
    if (data) {
      const userIdsWithJustSelectedJobType = Object.keys(data)
        .filter((userId) => data[userId].length === 1 && data[userId].includes(jobTypeId))
        .map((userId) => +userId);

      const usersNamesArray = users
        ?.filter((user) => {
          if (userIdsWithJustSelectedJobType.includes(user.id)) {
            return user.display_name;
          }
        })
        .toArray()
        .map((user) => user.display_name)
        .sort((name1, name2) => sortCompare(name1.toLowerCase(), name2.toLowerCase()));

      if (usersNamesArray.length > 3) {
        const firstThreeUsers = usersNamesArray.slice(0, 3).join(", ");
        const numberOfMore = usersNamesArray.length - 3;

        return `${firstThreeUsers} + ${numberOfMore} more`;
      }

      return usersNamesArray.join(", ");
    }
    return "";
  });

export const getAnniversaryUsers = createSelector(
  getActiveUsers,
  (state, date) => moment(date),
  (users, date) => {
    return users
      .filter((user) => moment.unix(user.get(`created_on`)).set("year", date.year()).isSame(date, "day"))
      .map((user) => ({
        id: user.id,
        name: user.display_name,
      }))
      .toArray();
  }
);

export const canCreateCalendarEvent = createSelector(
  (state) => state.calendars,
  getLoggedUser,
  (calendars, loggedUser) => {
    if (isClient(loggedUser)) return false;
    const hasCalendars =
      calendars &&
      Object.values(calendars).filter((calendar) => {
        return calendar?.members?.includes(loggedUser?.id) && !calendar.is_trashed;
      }).length > 0;
    if (!hasCalendars) return false;

    return true;
  }
);

export const canCreateCalendar = createSelector(getLoggedUser, (loggedUser) => {
  if (isClient(loggedUser)) return false;

  return true;
});
export const getCompanyUsers = createSelector(
  (state, companyId) => getCompanyById(state, companyId),
  (state) => getAllUsers(state),
  (company, users) => {
    if (!company?.members?.length) return [];

    return users
      .toArray()
      .filter((user) => company.members.includes(user.id))
      .filter((user) => !user.is_archived && !user.is_trashed)
      .map((user) => user.toJS());
  }
);

export const getArchivedCompanyUsers = createSelector(
  (state, companyId) => getCompanyById(state, companyId),
  (state) => getAllUsers(state),
  (company, users) => {
    if (!company?.members?.length) return [];

    return users
      .toArray()
      .filter((user) => company.members.includes(user.id))
      .filter((user) => user.is_archived && !user.is_trashed)
      .map((user) => user.toJS());
  }
);

export const makeCompanyHasOwner = () =>
  createSelector(
    (state, companyId) => getCompanyUsers(state, companyId),
    (users) => {
      return users.some((user) => isOwner(user));
    }
  );

export const getUsersEmailIdMap = createSelector(getActiveUsers, (users) => {
  return users.reduce((prev, next) => {
    prev[next.email] = next.id;
    return prev;
  }, {});
});
